import { createTheme } from '@mui/material/styles';

// Curated Clinical Accent Color Presets (Healthcare Industry Standards)
export const ACCENT_PRESETS = {
  indigo: {
    id: 'indigo',
    name: 'Medical Indigo',
    subtitle: 'Standard Clinical',
    main: '#393996',
    light1: '#DBDBEC',
    light2: '#DFDFEE',
    light3: '#F0F0F7',
    light4: '#F7F7FB',
    light5: '#F8F9FD',
    dark: '#27276B',
    gradient: 'linear-gradient(135deg, #393996 0%, #4F46E5 100%)',
    contrastText: '#FFFFFF',
  },
  teal: {
    id: 'teal',
    name: 'Clinical Teal',
    subtitle: 'Dental & Wellness',
    main: '#0D9488',
    light1: '#CCFBF1',
    light2: '#99F6E4',
    light3: '#F0FDFA',
    light4: '#F0FDFA',
    light5: '#F7FEFD',
    dark: '#0F766E',
    gradient: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)',
    contrastText: '#FFFFFF',
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Blue',
    subtitle: 'Modern Hospital EHR',
    main: '#0284C7',
    light1: '#BAE6FD',
    light2: '#7DD3FC',
    light3: '#F0F9FF',
    light4: '#F0F9FF',
    light5: '#F8FCFF',
    dark: '#0369A1',
    gradient: 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)',
    contrastText: '#FFFFFF',
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald Health',
    subtitle: 'General Practice & Pharmacy',
    main: '#059669',
    light1: '#A7F3D0',
    light2: '#6EE7B7',
    light3: '#ECFDF5',
    light4: '#ECFDF5',
    light5: '#F7FEFA',
    dark: '#047857',
    gradient: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
    contrastText: '#FFFFFF',
  },
  purple: {
    id: 'purple',
    name: 'Royal Amethyst',
    subtitle: 'Specialized Care',
    main: '#7C3AED',
    light1: '#DDD6FE',
    light2: '#C4B5FD',
    light3: '#F5F3FF',
    light4: '#FAF5FF',
    light5: '#FCFAFF',
    dark: '#6D28D9',
    gradient: 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)',
    contrastText: '#FFFFFF',
  },
  rose: {
    id: 'rose',
    name: 'Rose Clinical',
    subtitle: 'Cardiology & Diagnostics',
    main: '#E11D48',
    light1: '#FECDD3',
    light2: '#FDA4AF',
    light3: '#FFF1F2',
    light4: '#FFF1F2',
    light5: '#FFFAFA',
    dark: '#BE123C',
    gradient: 'linear-gradient(135deg, #E11D48 0%, #F43F5E 100%)',
    contrastText: '#FFFFFF',
  },
};

export const colors = {
  // Primary Colors (default)
  primaryMain: '#393996',
  primaryLight1: '#DBDBEC',
  primaryLight2: '#DFDFEE',
  primaryLight3: '#F0F0F7',
  primaryLight4: '#F7F7FB',
  primaryLight5: '#F8F9FD',

  // Action Colors
  actionPurpleDark: '#780078',
  actionPurpleLight: '#4C0099',
  actionGreen: '#006900',
  actionRed: '#963939',

  // Action Light Colors
  actionLightPurple1: '#F2E7F2',
  actionLightPurple2: '#E9E3F0',
  actionLightGreen: '#DBECDB',
  actionLightRed: '#ECDBDB',

  // Grey Colors
  black: '#000000',
  darkGray: '#4D4D4D',
  mediumGray: '#DBDBDB',
  lightGray: '#E6E6E6',
  extraLightGray: '#FAFAFA',
  white: '#FFFFFF',
};

export const typography = {
  fontFamily: 'Google Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',

  h1: {
    fontSize: '20px',
    lineHeight: '25px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '18px',
    lineHeight: '23px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '16px',
    lineHeight: '21px',
    fontWeight: 600,
    letterSpacing: '0',
  },
  h4: {
    fontSize: '15px',
    lineHeight: '20px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontSize: '15px',
    lineHeight: '20px',
    fontWeight: 600,
    letterSpacing: '0',
  },
  h6: {
    fontSize: '14px',
    lineHeight: '18px',
    fontWeight: 600,
    letterSpacing: '0',
  },
  body1: {
    fontSize: '14px',
    lineHeight: '21px',
    fontWeight: 500,
    letterSpacing: '0',
  },
  body2: {
    fontSize: '14px',
    lineHeight: '18px',
    fontWeight: 400,
    letterSpacing: '0',
  },
  small: {
    fontSize: '14px',
    lineHeight: '21px',
    fontWeight: 400,
    letterSpacing: '0',
  },
  smallRegular: {
    fontSize: '13px',
    lineHeight: '17px',
    fontWeight: 400,
    letterSpacing: '0',
  },
};

export const mobileTypography = {
  fontFamily: 'Google Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  h1: {
    fontSize: '18px',
    lineHeight: '22px',
    fontWeight: 700,
  },
  h2: {
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 600,
  },
  h3: {
    fontSize: '15px',
    lineHeight: '19px',
    fontWeight: 600,
  },
  h4: {
    fontSize: '14px',
    lineHeight: '18px',
    fontWeight: 700,
  },
  h5: {
    fontSize: '14px',
    lineHeight: '18px',
    fontWeight: 600,
  },
  h6: {
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: 600,
  },
  body1: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 500,
  },
  body2: {
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: 400,
  },
  small: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 400,
  },
  smallRegular: {
    fontSize: '11px',
    lineHeight: '14px',
    fontWeight: 400,
  },
};

export const shadows = [
  'none',
  '0px 1px 3px 0px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.03)',
  '0px 4px 6px -1px rgba(0, 0, 0, 0.05), 0px 2px 4px -1px rgba(0, 0, 0, 0.03)',
  '0px 10px 15px -3px rgba(0, 0, 0, 0.06), 0px 4px 6px -2px rgba(0, 0, 0, 0.03)',
  '0px 20px 25px -5px rgba(0, 0, 0, 0.08), 0px 10px 10px -5px rgba(0, 0, 0, 0.03)',
];

export const darkShadows = [
  'none',
  '0px 1px 3px 0px rgba(0, 0, 0, 0.4)',
  '0px 4px 6px -1px rgba(0, 0, 0, 0.5)',
  '0px 10px 15px -3px rgba(0, 0, 0, 0.6)',
  '0px 20px 25px -5px rgba(0, 0, 0, 0.7)',
];

export const drawerWidth = 260;

/**
 * Builds dynamic healthcare theme based on user's appearance settings
 */
export const buildTheme = ({ mode = 'light', accentKey = 'indigo', density = 'comfortable' } = {}) => {
  const isDark = mode === 'dark';
  const accent = ACCENT_PRESETS[accentKey] || ACCENT_PRESETS.indigo;

  const dynamicPalette = {
    mode: isDark ? 'dark' : 'light',
    primary: {
      main: accent.main,
      light: accent.light2,
      dark: accent.dark,
      contrastText: accent.contrastText,
      light1: accent.light1,
      light2: accent.light2,
      light3: accent.light3,
      light4: accent.light4,
      light5: accent.light5,
      gradient: accent.gradient,
    },
    secondary: {
      main: '#64748B',
      light: '#94A3B8',
      dark: '#334155',
      contrastText: '#FFFFFF',
    },
    action: {
      purpleDark: colors.actionPurpleDark,
      purpleLight: colors.actionPurpleLight,
      green: colors.actionGreen,
      red: colors.actionRed,
      hover: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
      selected: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
    },
    actionLight: {
      purple1: colors.actionLightPurple1,
      purple2: colors.actionLightPurple2,
      green: colors.actionLightGreen,
      red: colors.actionLightRed,
    },
    background: isDark
      ? {
          default: '#0F172A', // Deep slate navy
          paper: '#1E293B',   // Dark surface
          subtle: '#111827',  // Inset surface
          sidebar: '#0B132B',
          card: '#1E293B',
        }
      : {
          default: '#F8FAFC', // Crisp light medical slate
          paper: '#FFFFFF',   // Pure white surface
          subtle: '#F1F5F9',  // Inset surface
          sidebar: '#FFFFFF',
          card: '#FFFFFF',
        },
    text: isDark
      ? {
          primary: '#F8FAFC',
          secondary: '#94A3B8',
          disabled: '#64748B',
        }
      : {
          primary: '#0F172A',
          secondary: '#64748B',
          disabled: '#94A3B8',
        },
    divider: isDark ? '#334155' : '#E2E8F0',
    gery: isDark
      ? {
          black: '#FFFFFF',
          darkGray: '#CBD5E1',
          mediumGray: '#64748B',
          lightGray: '#334155',
          extraLightGray: '#1E293B',
          white: '#1E293B',
        }
      : {
          black: colors.black,
          darkGray: colors.darkGray,
          mediumGray: colors.mediumGray,
          lightGray: colors.lightGray,
          extraLightGray: colors.extraLightGray,
          white: colors.white,
        },
  };

  const dynamicComponents = {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: dynamicPalette.background.default,
          color: dynamicPalette.text.primary,
          scrollbarColor: isDark ? '#334155 #0F172A' : '#CBD5E1 #F8FAFC',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: isDark ? '#0F172A' : '#F8FAFC',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? '#334155' : '#CBD5E1',
            borderRadius: '4px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '10px',
          fontWeight: 600,
          padding: density === 'compact' ? '6px 14px' : '8px 18px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '14px',
          borderColor: dynamicPalette.divider,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: dynamicPalette.background.paper,
          borderRadius: '16px',
          border: `1px solid ${dynamicPalette.divider}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
        },
      },
    },
  };

  const muiTheme = createTheme({
    palette: dynamicPalette,
    typography,
    shadows: isDark ? darkShadows : shadows,
    components: dynamicComponents,
    shape: {
      borderRadius: 12,
    },
  });

  muiTheme.mobileTypography = mobileTypography;
  muiTheme.accent = accent;
  muiTheme.density = density;

  return muiTheme;
};

// Default Static Theme instance for backwards-compatibility
export const palette = {
  type: 'light',
  primary: {
    main: colors.primaryMain,
    light1: colors.primaryLight1,
    light2: colors.primaryLight2,
    light3: colors.primaryLight3,
    light4: colors.primaryLight4,
    light5: colors.primaryLight5,
  },
  action: {
    purpleDark: colors.actionPurpleDark,
    purpleLight: colors.actionPurpleLight,
    green: colors.actionGreen,
    red: colors.actionRed,
  },
  actionLight: {
    purple1: colors.actionLightPurple1,
    purple2: colors.actionLightPurple2,
    green: colors.actionLightGreen,
    red: colors.actionLightRed,
  },
  gery: {
    black: colors.black,
    darkGray: colors.darkGray,
    mediumGray: colors.mediumGray,
    lightGray: colors.lightGray,
    extraLightGray: colors.extraLightGray,
    white: colors.white,
  },
  text: {
    primary: colors.black,
    secondary: colors.darkGray,
  },
  background: {
    default: colors.extraLightGray,
  },
};

const defaultTheme = buildTheme({ mode: 'light', accentKey: 'indigo', density: 'comfortable' });

export default defaultTheme;
